import React from "react";
import { useTranslation } from "next-i18next";
import { Heading, Flex, Box, Text, SimpleGrid } from "@chakra-ui/react";

import Avatar from "@/components/common/Avatar";
import BoxCard from "@/components/common/BoxCard";
import Map from "@/components/common/Map";
import { Event } from "@/types/event";
import { useBreakpointValue } from "@chakra-ui/media-query";
import HelpTooltip from "@/components/common/HelpTooltip";
import Link from "@/components/common/Link";
import { getFileUri } from "@/utils/helpers";

type Props = {
  eventData: Event;
  rounded?: boolean;
  accreditationSettings?: boolean;
};

const EventInformation = ({
  eventData,
  rounded = true,
  accreditationSettings = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation("common");

  return (
    <>
      {accreditationSettings && (
        <Heading as="h2" size="lg">
          <Flex align="center">
            {t("events.application")}
            <HelpTooltip label="accreditation.editHeading" />
          </Flex>
        </Heading>
      )}
      <BoxCard mb="1.25rem" borderRadius={rounded ? ".375rem" : 0}>
        <Flex direction={{ base: "column", md: "row" }}>
          <Flex
            pr={{ base: 0, md: "1.25rem" }}
            pb={{ base: "1.25rem", md: 0 }}
            align="center"
            flex="1 1 0%"
          >
            <Box position="relative">
              <Avatar
                src={eventData?.picture ? getFileUri(eventData?.picture) : null}
                name={eventData?.name || ""}
                size={useBreakpointValue({ base: "lg", md: "2xl" })}
              />
            </Box>
            <Box ml="1.25rem">
              <Heading size="lg" mb={1}>
                {eventData?.name}
              </Heading>
              {eventData?.pressOffice && (
                <>
                  <Text color="text.500" fontSize="xs">
                    {t("events.organizer")}:
                  </Text>
                  <Link to={`/offices/office/${eventData?.pressOffice?.id}`}>
                    <Text lineHeight={1.2}>{eventData?.pressOffice?.name}</Text>
                  </Link>
                </>
              )}
              {eventData?.locations?.length === 1 && (
                <Box mt={4}>
                  <Text>{eventData?.locations[0]?.name}</Text>
                  <Text color="text.500" fontSize="xs">
                    {eventData?.locations[0]?.address}
                  </Text>
                </Box>
              )}
            </Box>
          </Flex>
          {eventData?.locations?.length > 1 && (
            <Box
              borderColor="light.50"
              borderLeftWidth={{ base: 0, md: "1px" }}
              borderTopWidth={{ base: "1px", md: 0 }}
              pl={{ base: 0, md: 5 }}
              pt={{ base: 5, md: 0 }}
              flex={{ base: "none", md: 1 }}
              w={{ base: "100%", md: "auto" }}
            >
              <Text fontWeight="500" mt={{ base: 0, md: 3 }} mb={4}>
                {t("settings.places")}
              </Text>
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacingX={1}
                spacingY={3}
              >
                {eventData?.locations.map((location) => (
                  <Box key={location?.id}>
                    <Text>{location?.name}</Text>
                    <Text color="text.500" fontSize="xs">
                      {location?.address}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          )}
          <Box
            borderColor="light.50"
            borderTopWidth={{
              base: eventData?.locations?.length === 1 ? "1px" : 0,
              md: 0,
            }}
            borderLeftWidth={{
              base: 0,
              md: eventData?.locations?.length === 1 ? "1px" : 0,
            }}
            pl={{ base: 0, md: 5 }}
            pt={{ base: 5, md: 0 }}
            pb={{
              base: 0,
              md: 0,
            }}
            flex={{ base: "none", md: "1 1 20%" }}
            w={{ base: "100%", md: "auto" }}
          >
            <Box>
              <Map
                height="200px"
                zoom={13}
                scrollWheelZoom={false}
                markers={eventData?.locations?.map((location) => ({
                  lat: location.lat,
                  lng: location.lng,
                }))}
                rounded={rounded}
              />
            </Box>
          </Box>
        </Flex>
      </BoxCard>
    </>
  );
};

export default EventInformation;
